import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import AppConfigApi from '../api/AppConfigApi'

import {
  applyRuntimeClinicModeConfig,
  CLINIC_MODE_MEDICAL_SPA,
  CLINIC_MODE_MIXED,
  CLINIC_MODE_WELLNESS,
  getClinicMode,
  getDefaultSubmode,
  getStoredSubmode,
  persistSubmode
} from '../config/clinicMode'

const ClinicModeContext = createContext(null)

export const ClinicModeProvider = ({ children }) => {
  const [clinicMode, setClinicMode] = useState(() => getClinicMode())
  const [submode, setSubmodeState] = useState(() => {
    return getStoredSubmode() || getDefaultSubmode()
  })

  useEffect(() => {
    let mounted = true
    const load = async () => {
      const cfg = await AppConfigApi.getAppConfig()
      if (!mounted || !cfg) return
      applyRuntimeClinicModeConfig(cfg)
      const nextMode = getClinicMode()
      setClinicMode(nextMode)
      if (nextMode === CLINIC_MODE_MIXED && !getStoredSubmode()) {
        setSubmodeState(getDefaultSubmode())
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  const setSubmode = (nextSubmode) => {
    setSubmodeState(nextSubmode)
    persistSubmode(nextSubmode)
  }

  const value = useMemo(() => {
    const effectiveSubmode =
      clinicMode === CLINIC_MODE_MIXED
        ? (submode || getDefaultSubmode())
        : clinicMode
    const isWellnessEffective = effectiveSubmode === CLINIC_MODE_WELLNESS
    const isMedicalSpaEffective = effectiveSubmode === CLINIC_MODE_MEDICAL_SPA
    return {
      clinicMode,
      submode,
      setSubmode,
      effectiveSubmode,
      isMixedClinicMode: clinicMode === CLINIC_MODE_MIXED,
      isWellnessEffective,
      isMedicalSpaEffective,
      canUseAutoSort: !isMedicalSpaEffective,
      canUsePractitionerRoomSort: isWellnessEffective,
      canAccessInventory: !isWellnessEffective
    }
  }, [clinicMode, submode])

  return <ClinicModeContext.Provider value={value}>{children}</ClinicModeContext.Provider>
}

ClinicModeProvider.propTypes = {
  children: PropTypes.node
}

export const useClinicMode = () => {
  const ctx = useContext(ClinicModeContext)
  if (!ctx) {
    const clinicMode = getClinicMode()
    const effectiveSubmode =
      clinicMode === CLINIC_MODE_MIXED
        ? (getStoredSubmode() || getDefaultSubmode())
        : clinicMode
    const isWellnessEffective = effectiveSubmode === CLINIC_MODE_WELLNESS
    const isMedicalSpaEffective = effectiveSubmode === CLINIC_MODE_MEDICAL_SPA
    return {
      clinicMode,
      effectiveSubmode,
      isMixedClinicMode: clinicMode === CLINIC_MODE_MIXED,
      isWellnessEffective,
      isMedicalSpaEffective,
      canUseAutoSort: !isMedicalSpaEffective,
      canUsePractitionerRoomSort: isWellnessEffective,
      canAccessInventory: !isWellnessEffective
    }
  }
  return ctx
}
